import { useCallback, useEffect, useState } from "react";

import {
  Search,
  Filter,
  Users,
  UserCheck,
  CreditCard,
  IndianRupee,
  RefreshCw,
} from "lucide-react";

import { adminService } from "@/services/paymentService";
import { cn } from "@/lib/utils";

const PAYMENT_OPTIONS = [
  "all",
  "paid",
  "unpaid",
];

const STATUS_OPTIONS = [
  "all",
  "signup",
  "active",
  "disabled",
];

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-lg",
          accent
        )}
      >
        <Icon className="h-5 w-5" />
      </div>

      <div className="mt-4 font-heading text-2xl font-extrabold text-slate-900">
        {value}
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function statusClass(status) {
  const value = String(
    status || ""
  ).toLowerCase();

  if (
    value === "active" ||
    value === "customer"
  ) {
    return "bg-green-100 text-green-700";
  }

  if (
    value === "signup" ||
    value === "lead"
  ) {
    return "bg-blue-100 text-blue-700";
  }

  if (value === "disabled") {
    return "bg-red-100 text-red-700";
  }

  return "bg-slate-100 text-slate-700";
}

function formatMoney(amount, currency = "INR") {
  const numericAmount = Number(amount || 0);

  if (
    !Number.isFinite(numericAmount) ||
    numericAmount <= 0
  ) {
    return currency === "USD"
      ? "$0.00"
      : "₹0.00";
  }

  if (currency === "USD") {
    return `$${numericAmount.toFixed(2)}`;
  }

  return `₹${numericAmount.toFixed(2)}`;
}

export default function ReferralPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);

  const [search, setSearch] =
    useState("");

  const [status, setStatus] =
    useState("all");

  const [payment, setPayment] =
    useState("all");

  const [promoter, setPromoter] =
    useState("all");

  const [promoters, setPromoters] =
    useState([]);

  const [page, setPage] =
    useState(1);

  const [total, setTotal] =
    useState(0);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const limit = 10;

  /* =====================================================
     LOAD REFERRALS
  ===================================================== */

  const loadReferrals =
    useCallback(async () => {
      setLoading(true);

      try {
        const data =
          await adminService.referrals({
            page,
            limit,
            search,
            status,
            payment,
            promoter,
          });

        setRows(
          Array.isArray(data?.items)
            ? data.items
            : []
        );

        setTotal(
          Number(data?.total || 0)
        );

        setStats(
          data?.stats || null
        );

        if (
          Array.isArray(
            data?.promoters
          )
        ) {
          setPromoters(
            data.promoters
          );
        }
      } catch (error) {
        console.error(
          "Failed to load referrals:",
          error
        );

        setRows([]);
        setTotal(0);
        setStats(null);
      } finally {
        setLoading(false);
      }
    }, [
      page,
      search,
      status,
      payment,
      promoter,
    ]);

  /* =====================================================
     AUTO LOAD
  ===================================================== */

  useEffect(() => {
    const timer =
      setTimeout(() => {
        loadReferrals();
      }, 300);

    return () =>
      clearTimeout(timer);
  }, [loadReferrals]);

  /* =====================================================
     REFRESH
  ===================================================== */

  async function handleRefresh() {
    setRefreshing(true);

    try {
      await loadReferrals();
    } finally {
      setRefreshing(false);
    }
  }

  /* =====================================================
     CLEAR FILTERS
  ===================================================== */

  function clearFilters() {
    setSearch("");
    setStatus("all");
    setPayment("all");
    setPromoter("all");
    setPage(1);
  }

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div
      className="min-h-screen bg-slate-100"
      data-testid="admin-referral-page"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Referral Dashboard
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Manage FirstPromoter referrals,
              customers and payments.
            </p>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 transition disabled:opacity-50"
          >
            <RefreshCw
              className={cn(
                "h-4 w-4",
                refreshing &&
                  "animate-spin"
              )}
            />

            Refresh
          </button>
        </div>

        {/* =================================================
            STATS
        ================================================= */}

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">

          <StatCard
            icon={Users}
            label="Total Referrals"
            value={
              stats?.totalReferrals ??
              "—"
            }
            accent="bg-slate-900 text-[#F5A623]"
          />

          <StatCard
            icon={UserCheck}
            label="Active Customers"
            value={
              stats?.activeCustomers ??
              "—"
            }
            accent="bg-green-50 text-green-600"
          />

          <StatCard
            icon={CreditCard}
            label="Paid Referrals"
            value={
              stats?.paidReferrals ??
              "—"
            }
            accent="bg-blue-50 text-blue-600"
          />

          <StatCard
            icon={IndianRupee}
            label="Revenue"
            value={
              stats?.revenue !==
              undefined
                ? formatMoney(
                    stats.revenue,
                    stats.currency ||
                      "INR"
                  )
                : "—"
            }
            accent="bg-amber-50 text-amber-600"
          />
        </div>

        {/* =================================================
            FILTERS
        ================================================= */}

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">

            {/* SEARCH */}

            <div className="relative lg:col-span-2">

              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(
                    e.target.value
                  );
                }}
                placeholder="Search name, email or UID..."
                className="cet-input pl-9"
              />
            </div>

            {/* STATUS */}

            <div className="flex items-center gap-2">

              <Filter className="h-4 w-4 text-slate-400 shrink-0" />

              <select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(
                    e.target.value
                  );
                }}
                className="cet-input bg-white"
              >
                {STATUS_OPTIONS.map(
                  (item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item ===
                      "all"
                        ? "All referral status"
                        : item}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* PAYMENT */}

            <select
              value={payment}
              onChange={(e) => {
                setPage(1);
                setPayment(
                  e.target.value
                );
              }}
              className="cet-input bg-white"
            >
              {PAYMENT_OPTIONS.map(
                (item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item === "all"
                      ? "All payments"
                      : item === "paid"
                        ? "Paid"
                        : "Unpaid"}
                  </option>
                )
              )}
            </select>

            {/* PROMOTER */}

            <select
              value={promoter}
              onChange={(e) => {
                setPage(1);
                setPromoter(
                  e.target.value
                );
              }}
              className="cet-input bg-white"
            >
              <option value="all">
                All promoters
              </option>

              {promoters.map(
                (item) => (
                  <option
                    key={
                      item.id ||
                      item.name
                    }
                    value={
                      item.id ||
                      item.name
                    }
                  >
                    {item.name}
                  </option>
                )
              )}
            </select>
          </div>

          {/* CLEAR */}

          {(search ||
            status !== "all" ||
            payment !== "all" ||
            promoter !== "all") && (
            <button
              type="button"
              onClick={
                clearFilters
              }
              className="mt-3 text-sm font-semibold text-amber-600 hover:text-amber-700"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* =================================================
            TABLE
        ================================================= */}

        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden overflow-x-auto">

          <table className="w-full min-w-[1150px]">

            <thead className="bg-slate-900 text-white">

              <tr>
                {[
                  "Referral",
                  "Email",
                  "Referred By",
                  "Status",
                  "Payment",
                  "Amount",
                  "Campaign",
                  "Created",
                ].map(
                  (heading) => (
                    <th
                      key={heading}
                      className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider"
                    >
                      {heading}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>

              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-slate-400"
                  >
                    Loading referrals…
                  </td>
                </tr>
              ) : rows.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-slate-400"
                  >
                    No referrals found.
                  </td>
                </tr>
              ) : (
                rows.map(
                  (referral) => {
                    const email =
                      referral.email ||
                      referral
                        .customer
                        ?.email ||
                      "—";

                    const name =
                      referral.name ||
                      referral
                        .customer?.name ||
                      email ||
                      "—";

                    const referredBy =
                      referral.referredBy ||
                      referral
                        .promoter
                        ?.name ||
                      "—";

                    const amount =
                      Number(
                        referral.saleAmount ??
                          referral.amount ??
                          referral.revenue ??
                          0
                      );

                    const currency =
                      referral.currency ||
                      "INR";

                    return (
                      <tr
                        key={
                          referral.id ||
                          referral._id ||
                          referral.uid ||
                          email
                        }
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >

                        {/* REFERRAL */}

                        <td className="px-4 py-3">

                          <div className="text-sm font-semibold text-slate-900">
                            {name}
                          </div>

                          <div className="text-xs text-slate-400 mt-1">
                            UID:{" "}
                            {referral.uid ||
                              "—"}
                          </div>
                        </td>

                        {/* EMAIL */}

                        <td className="px-4 py-3 text-sm text-slate-600">
                          {email}
                        </td>

                        {/* REFERRED BY */}

                        <td className="px-4 py-3">

                          <div className="text-sm font-semibold text-slate-700">
                            {referredBy}
                          </div>

                          {referral
                            .promoter
                            ?.email && (
                            <div className="text-xs text-slate-400 mt-1">
                              {
                                referral
                                  .promoter
                                  .email
                              }
                            </div>
                          )}
                        </td>

                        {/* STATUS */}

                        <td className="px-4 py-3">

                          <span
                            className={cn(
                              "inline-block rounded-full px-2.5 py-1 text-xs font-semibold capitalize",
                              statusClass(
                                referral.status
                              )
                            )}
                          >
                            {referral.status ||
                              "—"}
                          </span>
                        </td>

                        {/* PAYMENT */}

                        <td className="px-4 py-3">

                          <span
                            className={cn(
                              "inline-block rounded-full px-2.5 py-1 text-xs font-semibold",
                              referral.paid
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-600"
                            )}
                          >
                            {referral.paid
                              ? "Paid"
                              : "Unpaid"}
                          </span>
                        </td>

                        {/* AMOUNT */}

                        <td className="px-4 py-3 text-sm font-bold text-slate-900">
                          {formatMoney(
                            amount,
                            currency
                          )}
                        </td>

                        {/* CAMPAIGN */}

                        <td className="px-4 py-3 text-sm text-slate-600">
                          {referral.campaign
                            ?.name ||
                            referral.campaign ||
                            "—"}
                        </td>

                        {/* CREATED */}

                        <td className="px-4 py-3 text-sm text-slate-500">
                          {referral.createdAt
                            ? new Date(
                                referral.createdAt
                              ).toLocaleString(
                                "en-IN"
                              )
                            : "—"}
                        </td>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>
          </table>
        </div>

        {/* =================================================
            PAGINATION
        ================================================= */}

        <div className="mt-4 flex items-center justify-between text-sm">

          <span className="text-slate-500">
            Total: {total}
          </span>

          <div className="flex gap-2">

            <button
              disabled={page === 1}
              onClick={() =>
                setPage(
                  (current) =>
                    current - 1
                )
              }
              className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
            >
              Prev
            </button>

            <button
              disabled={
                page * limit >= total
              }
              onClick={() =>
                setPage(
                  (current) =>
                    current + 1
                )
              }
              className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>

          </div>
        </div>

      </div>
    </div>
  );
}